import _ from 'lodash'
import type { Element } from 'domhandler'
import type { CoreExportFormat } from '@wix/document-services-types'
import type { HtmlFile } from '../../../types'
import type { ComponentNode, InnerElementNode } from '../../../traversal/types'
import type { TagRegistry } from '../../../tagRegistry'
import { HtmlBuilder } from '../../../htmlBuilder/HtmlBuilder'
import { traverseComponents } from '../../../traversal/traverse'
import { getComponentTypeKey } from '../createRegistry'
import { WIX_INNER_ELEMENT_TAG } from '../constants'

const validateInput = (componentExport: CoreExportFormat): void => {
    const { rootComponent, components } = componentExport

    if (!rootComponent) {
        throw new Error('CoreExportFormat must have a rootComponent')
    }

    if (!components?.[rootComponent]) {
        throw new Error(`Root component "${rootComponent}" not found in components`)
    }
}

const getTagForComponent = (component: ComponentNode, id: string, registry: TagRegistry): string => {
    const typeKey = getComponentTypeKey(component)
    if (!typeKey) {
        throw new Error(`Component "${id}" has no componentType or type`)
    }

    const tag = registry.getTagByType(typeKey)
    if (!tag) {
        throw new Error(`Component type "${typeKey}" is not registered in TagRegistry`)
    }

    return tag
}

const buildAttributes = (component: ComponentNode, id: string): Record<string, string> => {
    if (!component.type) {
        throw new Error(`Component "${id}" missing required type`)
    }

    const attrs: Record<string, string> = { id, 'data-type': component.type as string }

    const { data } = component
    if (_.isPlainObject(data) && !_.isEmpty(data)) {
        attrs['data-props'] = JSON.stringify(data)
    }

    return attrs
}

const buildInnerElementAttributes = (element: InnerElementNode, key: string): Record<string, string> => {
    const attrs: Record<string, string> = { 'data-name': key }

    if (element.id) {
        attrs.id = element.id
    }

    const { data } = element
    if (_.isPlainObject(data) && !_.isEmpty(data)) {
        attrs['data-props'] = JSON.stringify(data)
    }

    return attrs
}

export const generateStructure = (componentExport: CoreExportFormat, registry: TagRegistry): HtmlFile => {
    validateInput(componentExport)

    const { rootComponent, components } = componentExport
    const rootComp = components[rootComponent!] as ComponentNode
    const rootTag = getTagForComponent(rootComp, rootComponent!, registry)
    const rootAttrs = buildAttributes(rootComp, rootComponent!)
    const builder = new HtmlBuilder(rootTag, rootAttrs)

    traverseComponents<Element>(componentExport, {
        onComponent: ({ id, component, depth, parentContext, traverseInnerElements }) => {
            const componentElement =
                depth === 0
                    ? builder.getRoot()
                    : builder.addElement(
                          parentContext!,
                          getTagForComponent(component, id, registry),
                          buildAttributes(component, id),
                      )

            traverseInnerElements<Element>(({ element, key, parentContext: ieParent }) => {
                const ieAttrs = buildInnerElementAttributes(element, key)
                const ieParentElement = ieParent ?? componentElement
                return builder.addElement(ieParentElement, WIX_INNER_ELEMENT_TAG, ieAttrs)
            })

            return componentElement
        },
    })

    return {
        type: 'html',
        content: builder.build(),
    }
}
